/**
 * Citation template presets — shipped as static seed data, clearly
 * labelled `source: 'preset'` so the product never claims convention
 * authority. Every preset is editable by the lawyer (P1.B Settings
 * UI); the LLM-derived defaults are starting points only.
 *
 * **DO NOT add a juri preset without validation by a practising
 * lawyer in that juri** — see `feedback_legal_convention_lawyer_validation.md`.
 * FR presets are validated by the founder via current production
 * usage (NF Z44-005 has shipped on the editor surface since Sprint 3).
 * MA-fr is a candidate starter — lawyer sign-off required before this
 * is promoted from `candidate` to `validated` in the docs.
 *
 * Placeholder syntax in `inlineFormat`:
 *   - `{code}`    → resolved code abbreviation (e.g. `C. civ.`)
 *   - `{article}` → resolved article number   (e.g. `1240`)
 *   - `\u00A0`    → non-breaking space (literal in source)
 *
 * Author convention: keep `{code}` and `{article}` separated by a
 * literal regular space + comma (the breakable point), and NBSP-link
 * `art.` to its number with `\u00A0` so a narrow surface wraps at the
 * comma rather than mid-citation.
 */
import type {
  CitationJurisdiction,
  CitationLanguage,
  CitationTemplate,
} from './entity'

/** All shipped presets, keyed by id for direct lookup. */
export const PRESETS: ReadonlyArray<CitationTemplate> = [
  // ─── France ────────────────────────────────────────────────────────
  {
    id: 'fr-fr-nf-z44-005',
    label: 'NF Z44-005 (Dalloz)',
    juris: 'fr',
    language: 'fr',
    // `C. civ., art. 1240` — code first per French legal convention.
    inlineFormat: '{code}, art.\u00A0{article}',
    source: 'preset',
  },
  {
    id: 'fr-fr-article-first',
    label: 'Article en tête',
    juris: 'fr',
    language: 'fr',
    // `art. 1240, C. civ.` — alternative that some practitioners use
    // when emphasising the article over the code.
    inlineFormat: 'art.\u00A0{article}, {code}',
    source: 'preset',
  },

  // ─── Maroc (francophone) — candidate, pending lawyer validation ────
  {
    id: 'ma-fr-default',
    label: 'Maroc (français) — candidat',
    juris: 'ma',
    language: 'fr',
    // `DOC, art. 100` — mirrors the French Dalloz form on the
    // assumption that francophone MA lawyers cite similarly. Subject
    // to validation by a practising MA lawyer before promotion.
    inlineFormat: '{code}, art.\u00A0{article}',
    source: 'preset',
  },
]

/** Default template ids per `(juris, language)`. Lawyers can override
 *  via Settings (P1.B) — this is the deterministic fallback the hook
 *  uses when no user choice exists for the bucket. */
const DEFAULT_PRESET_IDS: Record<string, string> = {
  'fr:fr': 'fr-fr-nf-z44-005',
  'ma:fr': 'ma-fr-default',
}

/** Stable bucket key — single source of truth for the `(juris, lang)`
 *  → preset id mapping. Future P1.B persistence layer will key user
 *  overrides by the same bucket. */
export function bucketKey(
  juris: CitationJurisdiction,
  language: CitationLanguage,
): string {
  return `${juris}:${language}`
}

/** Resolve the default preset for a `(juris, lang)` bucket. Returns
 *  `null` when no preset exists for the combo — caller decides
 *  fallback behaviour (typically: fall through to FR NF Z44-005). */
export function getDefaultPreset(
  juris: CitationJurisdiction,
  language: CitationLanguage,
): CitationTemplate | null {
  const id = DEFAULT_PRESET_IDS[bucketKey(juris, language)]
  if (!id) return null
  return PRESETS.find((p) => p.id === id) ?? null
}

/** Look up a preset by id — used by Settings when the lawyer picks a
 *  named alternative (e.g. `fr-fr-article-first`). */
export function getPresetById(id: string): CitationTemplate | null {
  return PRESETS.find((p) => p.id === id) ?? null
}

/** List presets matching a `(juris, lang)` bucket — used by the
 *  Settings UI to populate the "Choose a preset" dropdown. */
export function listPresetsForBucket(
  juris: CitationJurisdiction,
  language: CitationLanguage,
): CitationTemplate[] {
  return PRESETS.filter((p) => p.juris === juris && p.language === language)
}
